import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginCredentials } from '../types/user';
import { authApi } from '../api/auth.api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  selectedCompanyId: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  setUser: (user: User | null) => void;
  setSelectedCompanyId: (companyId: string | null) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCompanyId, setSelectedCompanyIdState] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedCompanyId = localStorage.getItem('selectedCompanyId');

    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        if (storedCompanyId) {
          setSelectedCompanyIdState(storedCompanyId);
        }
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('selectedCompanyId');
      }
    }

    setIsLoading(false);
  }, []);

  const login = async (credentials: LoginCredentials): Promise<void> => {
    const response = await authApi.login(credentials);
    const { user, token } = response.data;

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
  };

  const logout = () => {
    authApi.logout().catch(() => {
      // Ignore errors on logout
    });

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedCompanyId');
    setUser(null);
    setSelectedCompanyIdState(null);
  };

  const setSelectedCompanyId = (companyId: string | null) => {
    setSelectedCompanyIdState(companyId);
    if (companyId) {
      localStorage.setItem('selectedCompanyId', companyId);
    } else {
      localStorage.removeItem('selectedCompanyId');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        selectedCompanyId,
        login,
        logout,
        setUser,
        setSelectedCompanyId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
